import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Grid,
  Box,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { IStore } from '../../store/IStore';
import { thunkGetPaginatedSegments } from '../../store/actions/SegmentActions';
import { thunkGetAllCircles } from '../../store/actions/CircleActions';
import { thunkGetAllCouponTemplates } from '../../store/actions/CouponActions';
import { thunkCreateSuccessNotification, thunkCreateErrorNotification } from '../../store/actions/NotificationActions';
import { SegmentDto } from '../../store/models/segments/SegmentDto';
import { CircleDto } from '../../store/models/circle/CircleDto';
import { CouponTemplateApiResponse } from '../../store/models/coupon/CouponManagementDto';
import MenuLayout from '../app/MenuLayout';
import { UrlPathCampaignExperimental } from '../../api/url';
import { Api } from '../../api/Api';
import { CampaignMapper } from '../../store/models/campaignExperimental/CampaignMapper';
import {
  CampaignFormDto,
  CouponType,
  CouponValidityType,
  ExecutionMethod,
  EventType as CampaignEventTypeEnum,
  TargetAudienceType as CampaignTargetAudienceType,
} from '../../store/models/campaignExperimental/CampaignFormDto';
import { CampaignExperimentalStatus } from '../../store/models/campaignExperimental/CampaignExperimentalDto';
import CampaignBasicInfo from './create/CampaignBasicInfo';
import TargetAudienceSelection, { TargetAudienceType as SelectionAudienceType } from './create/TargetAudienceSelection';
import CouponSelection from './create/CouponSelection';
import NotificationPlan from './create/NotificationPlan';
import CampaignTiming from './create/CampaignTiming';

// Type aliases
type CampaignType = 'ONE_TIME' | 'RECURRING' | '';
type AudienceType = 'CIRCLE' | 'SEGMENT' | 'ALL' | '';
type LocalEventType = 'BIRTHDAY' | 'CIRCLE_UPGRADE' | 'EMAIL_OPT_IN' | 'REGISTRATION' | '';
type NotificationPlanType = 'EMAIL' | 'PUSH' | 'BOTH' | '';

// Assignment interfaces
interface SegmentOption {
  id: string;
  name: string;
  memberCount: number;
  description: string;
}

interface CircleOption {
  id: string;
  name: string;
  memberCount: number;
  description: string;
}

interface CampaignFormData {
  name: string;
  description: string;
   type: CampaignType;
  couponTemplateNumber: string;
  audienceType: AudienceType;
  executionTime: string;
  eventType: LocalEventType;
  offsetDays: number | null;
  executionHour: string;
  startDate: string;
  endDate: string;
  notificationPlan: NotificationPlanType;
  targets: string[];
  couponType: CouponType;
  couponValidityType: CouponValidityType;
  validityDays: number | null;
  fixedEndDate: string;
}

const mapAudienceTypeToTarget = (audienceType: AudienceType): CampaignTargetAudienceType => {
  switch (audienceType) {
    case 'SEGMENT':
      return CampaignTargetAudienceType.SEGMENT;
    case 'CIRCLE':
      return CampaignTargetAudienceType.CIRCLES;
    default:
      return CampaignTargetAudienceType.ALL_CUSTOMERS;
  }
};

const mapTargetAudienceToLocal = (audienceType?: CampaignTargetAudienceType): AudienceType => {
  switch (audienceType) {
    case CampaignTargetAudienceType.SEGMENT:
      return 'SEGMENT';
    case CampaignTargetAudienceType.CIRCLES:
      return 'CIRCLE';
    default:
      return 'ALL';
  }
};

const mapLocalEventTypeToEnum = (eventType: LocalEventType): CampaignEventTypeEnum | undefined => {
  switch (eventType) {
    case 'BIRTHDAY':
      return CampaignEventTypeEnum.BIRTHDAY;
    case 'CIRCLE_UPGRADE':
      return CampaignEventTypeEnum.TIER_UPDATE;
    case 'EMAIL_OPT_IN':
      return CampaignEventTypeEnum.EMAIL_OPTIN;
    case 'REGISTRATION':
      return CampaignEventTypeEnum.REGISTRATION;
    default:
      return undefined;
  }
};

const mapEnumEventTypeToLocal = (eventType?: CampaignEventTypeEnum): LocalEventType => {
  switch (eventType) {
    case CampaignEventTypeEnum.TIER_UPDATE:
      return 'CIRCLE_UPGRADE';
    case CampaignEventTypeEnum.EMAIL_OPTIN:
      return 'EMAIL_OPT_IN';
    case CampaignEventTypeEnum.BIRTHDAY:
      return 'BIRTHDAY';
    case CampaignEventTypeEnum.REGISTRATION:
      return 'REGISTRATION';
    default:
      return '';
  }
};

const mapLocalFormToDto = (form: CampaignFormData): CampaignFormDto => {
  const executionMethod =
    form.type === 'ONE_TIME' ? ExecutionMethod.SCHEDULED : ExecutionMethod.EVENT_DRIVEN;
  const targetAudienceType = mapAudienceTypeToTarget(form.audienceType);

  const normalizeDate = (value?: string): string | undefined => {
    if (!value) return undefined;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    if (value.includes('T')) {
      return value.split('T')[0];
    }
    return value;
  };

  const normalizedStartDate = normalizeDate(form.startDate);
  const normalizedEndDate = normalizeDate(form.endDate);

  return {
    name: form.name,
    description: undefined,
    executionMethod,
    scheduledDate: executionMethod === ExecutionMethod.SCHEDULED ? form.executionTime || undefined : undefined,
    eventType: mapLocalEventTypeToEnum(form.eventType),
    birthdayDaysBefore: form.offsetDays ?? undefined,
    birthdayTime: form.executionHour || undefined,
    tierUpdateTransitions: [],
    couponType: form.couponType,
    couponNumber: form.couponTemplateNumber,
    startDate: normalizedStartDate,
    endDate: normalizedEndDate,
    couponValidityType: form.couponValidityType,
    validityDays: form.validityDays ?? undefined,
    fixedEndDate: normalizeDate(form.fixedEndDate),
    targetAudienceType,
    segmentNumbers:
      targetAudienceType === CampaignTargetAudienceType.SEGMENT ? form.targets : undefined,
    circleNumbers:
      targetAudienceType === CampaignTargetAudienceType.CIRCLES ? form.targets : undefined,
    notificationPlan: form.notificationPlan || undefined,
  };
};

const mapDtoToLocalForm = (dto: CampaignFormDto): CampaignFormData => {
  const type: CampaignType = dto.executionMethod === ExecutionMethod.SCHEDULED ? 'ONE_TIME' : 'RECURRING';
  const audienceType = mapTargetAudienceToLocal(dto.targetAudienceType);

  let targets: string[] = [];
  if (dto.targetAudienceType === CampaignTargetAudienceType.SEGMENT) {
    targets = dto.segmentNumbers || [];
  } else if (dto.targetAudienceType === CampaignTargetAudienceType.CIRCLES) {
    targets = dto.circleNumbers || [];
  }

  return {
    name: dto.name,
    description: '',
    type,
    couponTemplateNumber: dto.couponNumber || '',
    audienceType,
    executionTime: dto.scheduledDate || '',
    eventType: mapEnumEventTypeToLocal(dto.eventType),
    offsetDays: dto.birthdayDaysBefore ?? null,
    executionHour: dto.birthdayTime || '',
    startDate: dto.startDate || '',
    endDate: dto.endDate || '',
    notificationPlan: (dto.notificationPlan as NotificationPlanType) || '',
    couponType: dto.couponType || CouponType.FREE,
    couponValidityType: dto.couponValidityType || CouponValidityType.DEFAULT_FROM_CAMPAIGN,
    validityDays: dto.validityDays ?? null,
    fixedEndDate: dto.fixedEndDate || '',
    targets,
  };
};

// Constants
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_PAGE_START = 0;
const COUPON_PAGE_SIZE = 100;

// Helper functions to convert Redux state to UI format
const mapSegmentsToOptions = (segments: SegmentDto[]): SegmentOption[] => {
  return segments.map((segment: SegmentDto) => ({
    id: segment.externalSegmentId,
    name: segment.name,
    memberCount: segment.membershipCount || 0,
    description: ''
  }));
};

const mapCirclesToOptions = (circles: CircleDto[]): CircleOption[] => {
  return circles.map((circle: CircleDto) => ({
    id: circle.circleNumber,
    name: circle.circleName,
    memberCount: 0,
    description: ''
  }));
};

// Custom hooks
const useCustomerCounts = (formData: CampaignFormData, segments: SegmentOption[], circles: CircleOption[]) => {
  const [actualSegmentCount, setActualSegmentCount] = useState<number | null>(null);
  const [actualCircleCount, setActualCircleCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchSegmentCount = async () => {
      if (formData.audienceType === 'SEGMENT' && formData.targets.length > 0) {
        try {
          const segmentExternalIds = formData.targets.filter((id: string) => id);
          
          if (segmentExternalIds.length > 0) {
            const count = await Api.countAffectedMembersBySegments(segmentExternalIds);
            setActualSegmentCount(count);
          }
        } catch (error) {
          console.error('Error fetching segment count:', error);
          setActualSegmentCount(null);
        }
      } else {
        setActualSegmentCount(null);
      }
    };

    fetchSegmentCount();
  }, [formData.audienceType, formData.targets, segments]);

  useEffect(() => {
    const fetchCircleCount = async () => {
      if (formData.audienceType === 'CIRCLE' && formData.targets.length > 0) {
        try {
          const circleNumbers = formData.targets.filter((id: string) => id);
          
          if (circleNumbers.length > 0) {
            const count = await Api.countCustomersByCircleNumbers(circleNumbers);
            setActualCircleCount(count);
          }
        } catch (error) {
          console.error('Error fetching circle count:', error);
          setActualCircleCount(null);
        }
      } else {
        setActualCircleCount(null);
      }
    };

    fetchCircleCount();
  }, [formData.audienceType, formData.targets, circles]);

  return { actualSegmentCount, actualCircleCount };
};

// Component props with Redux
interface CampaignCreatePageProps extends ConnectedProps<typeof connector> {}

const CampaignCreatePage: React.FC<CampaignCreatePageProps> = (props) => {
  const navigate = useNavigate();
  const { campaignNumber } = useParams<{ campaignNumber?: string }>();
  const { t } = useTranslation();
  const {
    thunkGetPaginatedSegments,
    thunkGetAllCircles,
    thunkGetAllCouponTemplates,
    thunkCreateSuccessNotification,
    thunkCreateErrorNotification,
    paginatedSegments,
    circles: circlesData,
    allCouponTemplates,
  } = props;
  const isEditMode = Boolean(campaignNumber);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(campaignNumber));
  const campaignStatus = useRef<CampaignExperimentalStatus | null>(null);
  
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    type: 'ONE_TIME',
    couponTemplateNumber: '',
    audienceType: '',
    executionTime: '',
    eventType: '',
    offsetDays: null,
    executionHour: '',
    startDate: '',
    endDate: '',
    notificationPlan: '',
    targets: [],
    couponType: CouponType.FREE,
    couponValidityType: CouponValidityType.DEFAULT_FROM_CAMPAIGN,
    validityDays: null,
    fixedEndDate: '',
  });
  
  // Helper function to determine if all fields should be disabled
  const isEditingDisabled = useMemo(() => {
    if (!isEditMode) return false;
    
    const now = new Date();
    
    // For scheduled campaigns (ONE_TIME): disable if now >= execution datetime
    if (formData.type === 'ONE_TIME' && formData.executionTime) {
      const executionDate = new Date(formData.executionTime);
      if (now >= executionDate) {
        return true;
      }
    }
    
    // For event driven campaigns (RECURRING): disable if now > startDate (only disable after start date has passed)
    if (formData.type === 'RECURRING' && formData.startDate) {
      const startDate = new Date(formData.startDate);
      // Set time to start of day for startDate comparison
      startDate.setHours(0, 0, 0, 0);
      const currentDate = new Date(now);
      currentDate.setHours(0, 0, 0, 0);
      if (currentDate > startDate) {
        return true;
      }
    }
    
    return false;
  }, [isEditMode, formData.type, formData.executionTime, formData.startDate]);

  // Convert Redux state to UI format with useMemo to prevent unnecessary re-renders
  const segments = useMemo(() => 
    paginatedSegments?.segments ? mapSegmentsToOptions(paginatedSegments.segments) : []
  , [paginatedSegments]);
  
  const circles = useMemo(() => 
    circlesData ? mapCirclesToOptions(circlesData) : []
  , [circlesData]);

  // Data loading effect
  useEffect(() => {
    thunkGetPaginatedSegments(undefined, [], DEFAULT_PAGE_START, DEFAULT_PAGE_SIZE, 'name', 'asc');
    thunkGetAllCircles();
    thunkGetAllCouponTemplates(1, COUPON_PAGE_SIZE, 'name', 'asc', ['ACTIVE']);
  }, [thunkGetPaginatedSegments, thunkGetAllCircles, thunkGetAllCouponTemplates]);

  useEffect(() => {
    if (!campaignNumber) {
      setIsLoading(false);
      return;
    }

    const loadCampaign = async () => {
      setIsLoading(true);
      try {
        const apiResponse = await Api.getCampaignExperimentalByNumber(campaignNumber);
        const dto = CampaignMapper.mapApiResponseToFormData(apiResponse);
        setFormData(mapDtoToLocalForm(dto));
        campaignStatus.current = (apiResponse.status as CampaignExperimentalStatus) || null;
      } catch (error) {
        console.error('Error loading campaign:', error);
        thunkCreateErrorNotification(
          t('campaignExperimental.create.campaignLoadFailed', 'Failed to load campaign'),
          error
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadCampaign();
  }, [campaignNumber, thunkCreateErrorNotification, t]);

  type CampaignFormErrors = Partial<Record<keyof CampaignFormData, string>>;
  const [errors, setErrors] = useState<CampaignFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Use custom hooks
  const { actualSegmentCount, actualCircleCount } = useCustomerCounts(formData, segments, circles);
  const durationRef = useRef<HTMLDivElement>(null);
  const audienceRef = useRef<HTMLDivElement>(null);
  const couponRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Handlers
  const handleCampaignTypeChange = (type: CampaignType) => {
    setFormData((prev) => (prev.type === type ? prev : { ...prev, type }));
  };

  const handleEventTypeChange = (eventType: LocalEventType) => {
    setFormData(prev => ({
      ...prev,
      eventType,
      offsetDays: eventType === 'BIRTHDAY' ? null : prev.offsetDays,
      executionHour: eventType === 'BIRTHDAY' ? '' : prev.executionHour,
      ...(eventType === 'CIRCLE_UPGRADE'
        ? {
            audienceType: '',
            targets: [],
          }
        : {}),
    }));
  };

  // Generic toggle handler for targets
  const createTargetToggleHandler = () => (targetId: string) => {
    const isSelected = formData.targets.includes(targetId);
    const newTargets = isSelected
      ? formData.targets.filter(id => id !== targetId)
      : [...formData.targets, targetId];
    
    setFormData(prev => ({
      ...prev,
      targets: newTargets
    }));
  };

  // Assignment handlers
  const handleAssignmentTypeChange = (type: SelectionAudienceType) => {
    let mappedType: AudienceType;
    if (type === 'segments') {
      mappedType = 'SEGMENT';
    } else if (type === 'circles') {
      mappedType = 'CIRCLE';
    } else {
      mappedType = 'ALL';
    }
    
    setFormData(prev => ({
      ...prev,
      audienceType: mappedType,
      targets: mappedType === 'ALL' ? [] : prev.targets
    }));
  };

  const handleCircleToggleForAssignment = createTargetToggleHandler();

  const handleSegmentToggle = createTargetToggleHandler();

  // Convert coupon templates to UI format
  const availableCoupons = useMemo(() => {
    if (!allCouponTemplates?.data) return [];
    
    // Helper functions for coupon formatting
    const getCouponValue = (template: CouponTemplateApiResponse): string => {
      if (template.valueType === 'PERCENTAGE') {
        return `${template.discountPercentage}%`;
      }
      if (template.valueType === 'FIXED_AMOUNT') {
        return `€${template.discountAmount}`;
      }
      return t('campaignExperimental.create.gift');
    };

    const getCouponDescription = (template: CouponTemplateApiResponse): string => {
      if (template.valueType === 'POINTS') {
        const costsText = t('campaignExperimental.create.costs');
        const pointsText = t('campaignExperimental.create.points');
        return `${costsText} ${template.requiredPoints} ${pointsText}`;
      }
      return '';
    };
    
    return allCouponTemplates.data.map((template: CouponTemplateApiResponse) => ({
      id: template.couponTemplateNumber,
      name: template.name,
      type: template.valueType,
      value: getCouponValue(template),
      description: getCouponDescription(template),
      template: template
    }));
  }, [allCouponTemplates, t]);

  const selectedCoupon = useMemo(() => {
    if (!formData.couponTemplateNumber) return undefined;
    return availableCoupons.find((coupon) => coupon.id === formData.couponTemplateNumber);
  }, [availableCoupons, formData.couponTemplateNumber]);

  useEffect(() => {
    if (!formData.couponTemplateNumber) return;
    if (selectedCoupon) return;

    setFormData((prev) => ({
      ...prev,
      couponTemplateNumber: '',
    }));
  }, [formData.couponTemplateNumber, selectedCoupon]);

  const detectCouponType = (coupon?: { template: CouponTemplateApiResponse }): CouponType => {
    if (!coupon) {
      return CouponType.FREE;
    }
    const template = coupon.template;
    const isPurchasable =
      template.activationType === 'POINTS' || (template.requiredPoints ?? 0) > 0;
    return isPurchasable ? CouponType.PURCHASABLE : CouponType.FREE;
  };

  useEffect(() => {
    if (!selectedCoupon) {
      return;
    }

    const nextType = detectCouponType(selectedCoupon);
    const templateValidityDays = selectedCoupon.template.validityPeriodDays;
    
    setFormData((prev) => {
      const updates: Partial<CampaignFormData> = {};
      
      // Update coupon type if changed
      if (prev.couponType !== nextType) {
        updates.couponType = nextType;
      }
      
      // Pre-fill validity days when conditions are met
      const isScheduled = prev.type === 'ONE_TIME';
      const isEventDriven = prev.type === 'RECURRING';
      const isPurchasable = nextType === CouponType.PURCHASABLE;
      const isFree = nextType === CouponType.FREE;
      const isDaysAfterPurchase = prev.couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE;
      
      // For scheduled + purchasable: pre-fill when DAYS_AFTER_PURCHASE is selected
      // For event-driven + free: pre-fill immediately (validity type is always DAYS_AFTER_PURCHASE for this scenario)
      const shouldPreFill = 
        (isScheduled && isPurchasable && isDaysAfterPurchase) ||
        (isEventDriven && isFree && isDaysAfterPurchase);
      
      if (shouldPreFill && templateValidityDays && !prev.validityDays) {
        updates.validityDays = templateValidityDays;
      }
      
      // Only update if there are changes
      if (Object.keys(updates).length === 0) {
        return prev;
      }

      return {
        ...prev,
        ...updates,
      };
    });
  }, [selectedCoupon]);

  // Pre-fill validity days when coupon validity type changes to DAYS_AFTER_PURCHASE
  useEffect(() => {
    if (!selectedCoupon || formData.couponValidityType !== CouponValidityType.DAYS_AFTER_PURCHASE) {
      return;
    }

    const templateValidityDays = selectedCoupon.template.validityPeriodDays;
    const isScheduled = formData.type === 'ONE_TIME';
    const isEventDriven = formData.type === 'RECURRING';
    const isPurchasable = formData.couponType === CouponType.PURCHASABLE;
    const isFree = formData.couponType === CouponType.FREE;

    // Pre-fill for both scenarios: scheduled + purchasable OR event-driven + free
    const shouldPreFill = (isScheduled && isPurchasable) || (isEventDriven && isFree);

    if (shouldPreFill && templateValidityDays && !formData.validityDays) {
      setFormData((prev) => ({
        ...prev,
        validityDays: templateValidityDays,
      }));
    }
  }, [formData.couponValidityType, formData.type, formData.couponType, formData.validityDays, selectedCoupon]);

  // Pre-fill validity days when campaign type or coupon type changes for event-driven + free scenario
  useEffect(() => {
    if (!selectedCoupon) {
      return;
    }

    const templateValidityDays = selectedCoupon.template.validityPeriodDays;
    const isEventDriven = formData.type === 'RECURRING';
    const isFree = formData.couponType === CouponType.FREE;
    const isDaysAfterPurchase = formData.couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE;

    // For event-driven + free, always pre-fill when DAYS_AFTER_PURCHASE is set
    if (isEventDriven && isFree && isDaysAfterPurchase && templateValidityDays && !formData.validityDays) {
      setFormData((prev) => ({
        ...prev,
        validityDays: templateValidityDays,
      }));
    }
  }, [formData.type, formData.couponType, formData.couponValidityType, formData.validityDays, selectedCoupon]);


  type FormFieldValue = string | number | string[] | null | CouponValidityType | CouponType | undefined;
  
  const handleInputChange = (field: keyof CampaignFormData) => (value: FormFieldValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation helper functions
  const validateBasicInfo = (errors: CampaignFormErrors) => {
    if (!formData.name.trim()) {
      errors.name = t('campaignExperimental.create.validation.nameRequired');
    }
  };

  const validateEventDrivenFreeCouponValidity = (errors: CampaignFormErrors) => {
    if (formData.couponValidityType !== CouponValidityType.DAYS_AFTER_PURCHASE) {
      errors.couponValidityType = t('campaignExperimental.create.validation.couponValidityTypeRequired');
    }
    if (!formData.validityDays || formData.validityDays <= 0) {
      errors.validityDays = t('campaignExperimental.create.validation.validityDaysRequired');
    }
  };

  const validateScheduledPurchasableCouponValidity = (errors: CampaignFormErrors) => {
    if (formData.couponValidityType === CouponValidityType.FIXED_END_DATE) {
      if (!formData.fixedEndDate) {
        errors.fixedEndDate = t('campaignExperimental.create.validation.fixedEndDateRequired');
      } else if (formData.endDate && formData.fixedEndDate < formData.endDate) {
        errors.fixedEndDate = t('campaignExperimental.create.validation.fixedEndDateAfterCampaign');
      }
    } else if (formData.couponValidityType === CouponValidityType.DAYS_AFTER_PURCHASE) {
      if (!formData.validityDays || formData.validityDays <= 0) {
        errors.validityDays = t('campaignExperimental.create.validation.validityDaysRequired');
      }
    } else {
      errors.couponValidityType = t('campaignExperimental.create.validation.couponValidityTypeRequired');
    }
  };

  const validateCampaignTiming = (errors: CampaignFormErrors) => {
    if (!formData.startDate) {
      errors.startDate = t('campaignExperimental.create.validation.startDateRequired');
    }
    if (!formData.endDate) {
      errors.endDate = t('campaignExperimental.create.validation.endDateRequired');
    }
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      errors.endDate = t('campaignExperimental.create.validation.endDateAfterStart');
    }

    const isScheduled = formData.type === 'ONE_TIME';
    const isEventDriven = formData.type === 'RECURRING';
    const isFreeCoupon = formData.couponType === CouponType.FREE;
    const isPurchasableCoupon = formData.couponType === CouponType.PURCHASABLE;

    if (isEventDriven && isFreeCoupon) {
      validateEventDrivenFreeCouponValidity(errors);
    }

    if (isScheduled && isPurchasableCoupon) {
      validateScheduledPurchasableCouponValidity(errors);
    }
  };

  const validateExecutionTimeForOneTime = (errors: CampaignFormErrors) => {
    if (!formData.executionTime) {
      errors.executionTime = t('campaignExperimental.create.validation.executionTimeRequired', 'Execution time is required');
    }
  };

  const validateCouponSelection = (errors: CampaignFormErrors) => {
    if (!formData.couponTemplateNumber) {
      errors.couponTemplateNumber = t('campaignExperimental.create.validation.couponRequired');
    }
  };

  const validateTargetAudience = (errors: CampaignFormErrors) => {
    const shouldValidateTargetAudience = (
      formData.type === 'ONE_TIME' ||
      (formData.type === 'RECURRING' && formData.eventType !== 'CIRCLE_UPGRADE')
    );

    if (shouldValidateTargetAudience) {
      if (!formData.audienceType) {
        errors.audienceType = 'Audience type is required';
      } else if (formData.audienceType !== 'ALL' && formData.targets.length === 0) {
        errors.targets = 'Please select at least one target';
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: CampaignFormErrors = {};

    validateBasicInfo(newErrors);
    
    validateCampaignTiming(newErrors);

    if (formData.type === 'ONE_TIME') {
      validateExecutionTimeForOneTime(newErrors);
    }

    validateCouponSelection(newErrors);
    validateTargetAudience(newErrors);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const dto = mapLocalFormToDto(formData);

      if (isEditMode && campaignNumber) {
        await Api.updateCampaignExperimental(campaignNumber, dto);
        thunkCreateSuccessNotification(
          t('campaignExperimental.create.campaignUpdatedSuccessfully', 'Campaign updated successfully')
        );
      } else {
        await Api.createCampaignExperimental(dto);
        thunkCreateSuccessNotification(t('campaignExperimental.create.campaignCreatedSuccessfully'));
      }

      navigate(UrlPathCampaignExperimental);
    } catch (error) {
      console.error('Failed to save campaign:', error);
      const errorKey = isEditMode
        ? 'campaignExperimental.create.campaignUpdateFailed'
        : 'campaignExperimental.create.campaignCreationFailed';
      thunkCreateErrorNotification(t(errorKey, 'Failed to save campaign'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(UrlPathCampaignExperimental);
  };

  const pageTitle = isEditMode
    ? t('campaignExperimental.create.editCampaignPageTitle', 'Edit Campaign')
    : t('campaignExperimental.create.createCampaignPageTitle');

  if (isLoading) {
    return (
      <MenuLayout headline={pageTitle}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </MenuLayout>
    );
  }

  return (
    <MenuLayout headline={pageTitle}>
      <Grid container spacing={3}>
        {/* Campaign Basic Information */}
        <Grid item xs={12}>
          <CampaignBasicInfo
            name={formData.name}
            type={formData.type}
            eventType={formData.eventType}
            executionHour={formData.executionHour}
            daysOffset={formData.offsetDays}
            executionDateTime={formData.executionTime}
            onNameChange={(name) => handleInputChange('name')(name)}
            onCampaignTypeChange={(newType) => handleCampaignTypeChange(newType)}
            onEventTypeChange={(newEventType) => handleEventTypeChange(newEventType)}
            onBirthdayOffsetChange={(offset) => handleInputChange('offsetDays')(offset)}
            onBirthdayTimeChange={(time) => handleInputChange('executionHour')(time)}
            onExecutionDateChange={(value) => handleInputChange('executionTime')(value)}
            nameError={errors.name}
            disabled={isEditingDisabled}
          />
        </Grid>

        {/* Step 5: Coupon Selection (placed before timing like xplorers) */}
        <Grid item xs={12} md={12} ref={couponRef}>
          <CouponSelection
            value={formData.couponTemplateNumber}
            options={availableCoupons}
            isRecurring={formData.type === 'RECURRING'}
            onChange={(couponId) => handleInputChange('couponTemplateNumber')(couponId)}
            error={errors.couponTemplateNumber}
            disabled={isEditingDisabled}
            onCategoryChange={(category) =>
              setFormData((prev) => {
                const nextType = category === 'purchasable' ? CouponType.PURCHASABLE : CouponType.FREE;
                const selectedOption = availableCoupons.find(
                  (coupon) => coupon.id === prev.couponTemplateNumber
                );
                const selectionMatches =
                  selectedOption && detectCouponType(selectedOption) === nextType;

                return {
                  ...prev,
                  couponType: nextType,
                  couponTemplateNumber: selectionMatches ? prev.couponTemplateNumber : '',
                };
              })
            }
          />
        </Grid>

        {/* Campaign Timing */}
        <Grid item xs={12} md={12} ref={durationRef}>
          <CampaignTiming
            executionMethod={
              formData.type === 'ONE_TIME' ? ExecutionMethod.SCHEDULED : ExecutionMethod.EVENT_DRIVEN
            }
            couponType={formData.couponType}
            startDate={formData.startDate}
            endDate={formData.endDate}
            fixedEndDate={formData.fixedEndDate}
            couponValidityType={formData.couponValidityType}
            validityDays={formData.validityDays ?? undefined}
            onStartDateChange={(date) => handleInputChange('startDate')(date)}
            onEndDateChange={(date) => handleInputChange('endDate')(date)}
            onFixedEndDateChange={(date) => handleInputChange('fixedEndDate')(date || '')}
            onCouponValidityTypeChange={(value) => handleInputChange('couponValidityType')(value)}
            onValidityDaysChange={(value) => handleInputChange('validityDays')(value ?? null)}
            startDateError={errors.startDate}
            endDateError={errors.endDate}
            validityDaysError={errors.validityDays}
            fixedEndDateError={errors.fixedEndDate}
            couponValidityTypeError={errors.couponValidityType}
            disabled={isEditingDisabled}
          />
        </Grid>

        {/* Step 7: Target Audience */}
        {formData.eventType !== 'CIRCLE_UPGRADE' && (
          <Grid item xs={12} md={12} ref={audienceRef}>
            <TargetAudienceSelection
              type={(() => {
                if (formData.audienceType === 'ALL') return 'all';
                if (formData.audienceType === 'SEGMENT') return 'segments';
                if (formData.audienceType === 'CIRCLE') return 'circles';
                return '';
              })() as SelectionAudienceType}
              selectedSegments={formData.audienceType === 'SEGMENT' ? formData.targets : []}
              selectedCircles={formData.audienceType === 'CIRCLE' ? formData.targets : []}
              segments={segments}
              circles={circles}
              onTypeChange={(type) => handleAssignmentTypeChange(type)}
              onSegmentToggle={handleSegmentToggle}
              onCircleToggle={handleCircleToggleForAssignment}
              isLoading={segments.length === 0}
              error={(() => {
                if (errors.targets) return String(errors.targets);
                if (errors.audienceType) return String(errors.audienceType);
                return undefined;
              })()}
              estimatedCustomers={actualSegmentCount || undefined}
              estimatedCircleCustomers={actualCircleCount || undefined}
              disabled={isEditingDisabled}
            />
          </Grid>
        )}

        {/* Step 8: Notification Plan */}
        <Grid item xs={12} md={12} ref={notificationRef}>
          <NotificationPlan
            selectedOptions={(() => {
              if (formData.notificationPlan === 'EMAIL') return ['email'];
              if (formData.notificationPlan === 'PUSH') return ['push'];
              if (formData.notificationPlan === 'BOTH') return ['email', 'push'];
              return [];
            })()}
            onChange={(options) => {
              if (options.length === 0) {
                handleInputChange('notificationPlan')('');
              } else if (options.length === 1) {
                const notificationValue = options[0] === 'email' ? 'EMAIL' : 'PUSH';
                handleInputChange('notificationPlan')(notificationValue);
              } else {
                handleInputChange('notificationPlan')('BOTH');
              }
            }}
            error={errors.notificationPlan ? String(errors.notificationPlan) : undefined}
            disabled={isEditingDisabled}
          />
        </Grid>

        <Grid item xs={12} sx={{ mb: 2 }}>
          <Grid container justifyContent="flex-end" alignItems="center">
            <Grid item>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleBack}
                disabled={isSubmitting}
                sx={{ mr: 2 }}
              >
                {t('campaignExperimental.create.cancelButton')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Save />}
                onClick={handleSubmit}
                disabled={isSubmitting || isEditingDisabled}
              >
                {isSubmitting
                  ? t('campaignExperimental.create.creatingButton')
                  : isEditMode
                    ? t('campaignExperimental.create.saveCampaignButton', 'Save Campaign')
                    : t('campaignExperimental.create.createCampaignButton')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </MenuLayout>
  );
};

const mapStateToProps = (state: IStore) => ({
  paginatedSegments: state.segments.paginatedSegments,
  circles: state.circles.circles,
  allCouponTemplates: state.coupons.allCouponTemplates,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({
    thunkGetPaginatedSegments,
    thunkGetAllCircles,
    thunkGetAllCouponTemplates,
    thunkCreateSuccessNotification,
    thunkCreateErrorNotification,
  }, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(CampaignCreatePage);
